import React, { useEffect, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PhotoPicker from '../../components/PhotoPicker';
import PlayerAvatar from '../../components/ui/PlayerAvatar';
import api from '../../api';
import tokens from '../../theme/tokens';
import { resizeImage } from '../../utils/image';

const emailRegex = /^[A-Za-z0-9+_.-]+@(.+)$/;
const senhaRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d]{8,}$/;

const maskCpf = (v) => v.replace(/\D/g, '').slice(0, 11).replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
const maskTelefone = (v) => v.replace(/\D/g, '').slice(0, 11).replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d{1,4})$/, '$1-$2');

function cpfValido(valor) {
  const cpf = valor.replace(/\D/g, '');
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;
  const digito = (len) => {
    let soma = 0;
    for (let i = 0; i < len; i++) soma += Number(cpf[i]) * (len + 1 - i);
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };
  return digito(9) === Number(cpf[9]) && digito(10) === Number(cpf[10]);
}

const inicial = { nome: '', nickName: '', dataNascimento: '', telefone: '', cpf: '', torre: '', apartamento: '', email: '', password: '', confirmacao: '' };

const Secao = ({ numero, titulo, children }) => (
  <Box sx={{ mb: 3 }}>
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
      <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: tokens.color.brand, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 14 }}>{numero}</Box>
      <Typography variant="h6">{titulo}</Typography>
    </Stack>
    {children}
  </Box>
);

// Cadastro completo do jogador, com foto de perfil pela câmera ou galeria.
const RegisterPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(inicial);
  const [foto, setFoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [erros, setErros] = useState({});
  const [erroGeral, setErroGeral] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [concluido, setConcluido] = useState(false);

  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  const set = (campo, mascara) => (e) => {
    const valor = mascara ? mascara(e.target.value) : e.target.value;
    setForm({ ...form, [campo]: valor });
    if (erros[campo]) setErros({ ...erros, [campo]: undefined });
  };

  const escolherFoto = async (arquivo) => {
    const reduzida = await resizeImage(arquivo, { maxSize: 800 });
    setFoto(reduzida);
    setPreview(URL.createObjectURL(reduzida));
  };

  const validar = () => {
    const e = {};
    if (form.nome.trim().split(/\s+/).length < 2) e.nome = 'Informe nome e sobrenome';
    if (!form.dataNascimento) e.dataNascimento = 'Obrigatório';
    if (form.telefone.replace(/\D/g, '').length < 10) e.telefone = 'Telefone inválido';
    if (!cpfValido(form.cpf)) e.cpf = 'CPF inválido';
    if (!form.torre) e.torre = 'Obrigatório';
    if (!form.apartamento) e.apartamento = 'Obrigatório';
    if (!form.email.match(emailRegex)) e.email = 'E-mail inválido';
    if (!form.password.match(senhaRegex)) e.password = 'Mínimo 8 caracteres com maiúsculas, minúsculas e números (sem símbolos)';
    if (form.password !== form.confirmacao) e.confirmacao = 'As senhas não conferem';
    setErros(e);
    return Object.keys(e).length === 0;
  };

  const enviar = async (event) => {
    event.preventDefault();
    setErroGeral('');
    if (!validar()) {
      setErroGeral('Revise os campos destacados.');
      return;
    }
    if (!foto) {
      setErroGeral('Adicione uma foto de perfil — ela aparece nos cards do torneio.');
      return;
    }
    setEnviando(true);
    try {
      const { confirmacao, ...dados } = form;
      const data = new FormData();
      Object.entries(dados).forEach(([chave, valor]) => data.append(chave, valor));
      data.append('file', foto);
      await api.post('user/store', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setConcluido(true);
    } catch (error) {
      setErroGeral(error.response?.data?.error || 'Não foi possível concluir o cadastro.');
    }
    setEnviando(false);
  };

  const fundo = { minHeight: '100vh', py: { xs: 3, sm: 6 }, px: 2, background: `linear-gradient(180deg, ${tokens.color.ink} 0, ${tokens.color.ink} 280px, ${tokens.color.bg} 280px)` };

  if (concluido) {
    return (
      <Box sx={fundo}>
        <Card sx={{ maxWidth: 480, mx: 'auto', p: 4, textAlign: 'center' }}>
          <CheckCircleRoundedIcon sx={{ fontSize: 64, color: 'success.main' }} />
          <Typography variant="h5" sx={{ mt: 1 }}>Cadastro enviado!</Typography>
          <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
            A diretoria vai aprovar seu acesso. Você receberá um e-mail em <strong>{form.email}</strong> assim que estiver liberado.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/login')}>Ir para o login</Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={fundo}>
      <Box sx={{ maxWidth: 760, mx: 'auto' }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ color: '#fff', mb: 3 }}>
          <Box component={RouterLink} to="/" sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: '#fff', display: 'grid', placeItems: 'center' }}>
            <Box component="img" src="/logo_rfc.png" alt="Riviera FC" sx={{ width: 38 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ lineHeight: 1 }}>Criar conta</Typography>
            <Typography variant="body2" sx={{ opacity: 0.75 }}>Pelada Riviera · Riviera Boa Viagem</Typography>
          </Box>
        </Stack>

        <Card component="form" onSubmit={enviar} noValidate sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Secao numero={1} titulo="Foto de perfil">
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center">
              <PlayerAvatar src={preview} name={form.nome || 'Você'} size={120} ring />
              <Box sx={{ flexGrow: 1, width: '100%' }}>
                <Typography color="text.secondary" sx={{ mb: 1.5 }}>
                  Tire uma selfie ou escolha uma foto do dispositivo. Ela aparece nos cards dos jogadores do torneio.
                </Typography>
                <PhotoPicker onPick={escolherFoto} fullWidth />
              </Box>
            </Stack>
          </Secao>
          <Divider sx={{ mb: 3 }} />

          <Secao numero={2} titulo="Dados pessoais">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}><TextField label="Nome completo" value={form.nome} onChange={set('nome')} error={Boolean(erros.nome)} helperText={erros.nome} required fullWidth autoFocus /></Grid>
              <Grid item xs={12} sm={4}><TextField label="Apelido (opcional)" value={form.nickName} onChange={set('nickName')} fullWidth /></Grid>
              <Grid item xs={12} sm={4}><TextField label="Data de nascimento" type="date" value={form.dataNascimento} onChange={set('dataNascimento')} error={Boolean(erros.dataNascimento)} helperText={erros.dataNascimento} InputLabelProps={{ shrink: true }} required fullWidth /></Grid>
              <Grid item xs={12} sm={4}><TextField label="Celular (WhatsApp)" value={form.telefone} onChange={set('telefone', maskTelefone)} error={Boolean(erros.telefone)} helperText={erros.telefone} inputProps={{ inputMode: 'tel' }} required fullWidth /></Grid>
              <Grid item xs={12} sm={4}><TextField label="CPF" value={form.cpf} onChange={set('cpf', maskCpf)} error={Boolean(erros.cpf)} helperText={erros.cpf || 'Usado no PIX das mensalidades'} inputProps={{ inputMode: 'numeric' }} required fullWidth /></Grid>
            </Grid>
          </Secao>
          <Divider sx={{ mb: 3 }} />

          <Secao numero={3} titulo="Moradia">
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField select label="Torre" value={form.torre} onChange={set('torre')} error={Boolean(erros.torre)} helperText={erros.torre} required fullWidth>
                  <MenuItem value="Acqua">Acqua</MenuItem>
                  <MenuItem value="Natura">Natura</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}><TextField label="Apartamento" value={form.apartamento} onChange={set('apartamento')} error={Boolean(erros.apartamento)} helperText={erros.apartamento} inputProps={{ inputMode: 'numeric' }} required fullWidth /></Grid>
            </Grid>
          </Secao>
          <Divider sx={{ mb: 3 }} />

          <Secao numero={4} titulo="Acesso">
            <Grid container spacing={2}>
              <Grid item xs={12}><TextField label="E-mail" type="email" autoComplete="email" value={form.email} onChange={set('email')} error={Boolean(erros.email)} helperText={erros.email} required fullWidth /></Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Senha"
                  type={mostrarSenha ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={form.password}
                  onChange={set('password')}
                  error={Boolean(erros.password)}
                  helperText={erros.password || '8+ caracteres, maiúscula, minúscula e número'}
                  required
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setMostrarSenha((v) => !v)} edge="end" aria-label="Mostrar senha">
                          {mostrarSenha ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}><TextField label="Confirmar senha" type={mostrarSenha ? 'text' : 'password'} autoComplete="new-password" value={form.confirmacao} onChange={set('confirmacao')} error={Boolean(erros.confirmacao)} helperText={erros.confirmacao} required fullWidth /></Grid>
            </Grid>
          </Secao>

          {erroGeral && <Alert severity="error" sx={{ mb: 2 }}>{erroGeral}</Alert>}
          <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ sm: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Já tem conta? <Link component={RouterLink} to="/login" fontWeight={600}>Entrar</Link>
            </Typography>
            <Button type="submit" variant="contained" size="large" disabled={enviando} sx={{ minWidth: 200 }}>
              {enviando ? <CircularProgress size={24} color="inherit" /> : 'Criar conta'}
            </Button>
          </Stack>
        </Card>
      </Box>
    </Box>
  );
};

export default RegisterPage;
